import type { OnInit, Signal } from '@angular/core';
import { Component, inject, Injector, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalService,
  OnyxSectionComponent,
} from '@onyx/angular';
import { filter, map } from 'rxjs';
import { DocumentsFormListComponent } from './documents-form-list/documents-form-list.component';
import type {
  DocumentForm,
  DocumentFormGroup,
  DocumentModalData,
  DocumentModalResult,
} from './documents-modal-form/documents-modal-form.component';
import { DocumentsModalFormComponent } from './documents-modal-form/documents-modal-form.component';

export type DocumentsFormGroup = ReturnType<
  typeof DocumentsFormComponent.buildForm
>;

@Component({
  selector: 'app-documents-form',
  imports: [
    OnyxSectionComponent,
    DocumentsFormListComponent,
    TranslatePipe,
    OnyxButtonComponent,
  ],
  templateUrl: './documents-form.component.html',
  styleUrl: './documents-form.component.scss',
})
export class DocumentsFormComponent implements OnInit {
  private readonly modalService = inject(OnyxModalService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);

  public readonly form = input.required<DocumentsFormGroup>();

  protected readonly I18N = 'forms.documents';

  protected documents!: Signal<DocumentForm[]>;

  public ngOnInit(): void {
    this.documents = toSignal(
      this.form().valueChanges.pipe(map(() => this.form().getRawValue())),
      { initialValue: this.form().getRawValue(), injector: this.injector },
    );
  }

  protected addDocument(): void {
    this.modalService
      .open<DocumentModalData, DocumentModalResult>(
        DocumentsModalFormComponent,
        { mode: OnyxFormMode.ADD },
      )
      .result.pipe(filter((document) => document != null))
      .subscribe((document) => {
        const control = DocumentsModalFormComponent.buildForm(this.fb);
        control.setValue(document);
        this.form().push(control);
      });
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<DocumentFormGroup>([]);
  }
}
